import { Injectable } from '@angular/core';

import { calculateTotals_210000 as calc_210000 } from './calculation/role8-210000.calc';
import { calculateTotals_210100 as calc_210100 } from './calculation/role9-210100.calc';
import { calculateTotals_220000 as calc_220000 } from './calculation/role10-220000.calc';
import { calculateTotals_220100 as calc_220100 } from './calculation/role11-220100.calc';
import { calculateTotals_310000 as calc_310000 } from './calculation/role12-310000.calc';
import { calculateTotals_310100 as calc_310100 } from './calculation/role13-310100.calc';
import { calculateTotals_320000 as calc_320000 } from './calculation/role14-320000.calc';
import { calculateTotals_320100 as calc_320100 } from './calculation/role15-320100.calc';
import { calculateTotals_410000 as calc_410000 } from './calculation/role15-410000.calc';
import { calculateTotals_420000 as calc_420000 } from './calculation/role16-420000.calc';
import { calculateTotals_510000 as calc_510000 } from './calculation/role17-510000.calc';
import { calculateTotals_520000 as calc_520000 } from './calculation/role18-520000.calc';
import { calculateTotals_610000 as calc_610000 } from './calculation/role19-610000.calc';

/**
 * Calculated totals for every sheet that has them.
 *
 * This service is a **thin facade**: each method delegates to a pure function in
 * `./calculation/`, one file per sheet. It stays an injectable service so calling
 * code keeps a single dependency, and so totals remain reachable before a component
 * has initialised (they run when API data loads, driven by `filing-form-container`).
 *
 * **Add a new sheet's totals as a new `calculation/roleN-NNNNNN.calc.ts` file** plus a
 * delegate here — never by growing this file. Until 2026-08-24 this was a single
 * 5,557-line class holding all 13 sheets, so reading any one calculation cost the
 * whole file.
 *
 * The pure functions are exported, so a validator or utility needing one calculation
 * can import it directly rather than injecting the whole service.
 *
 * Each delegate is typed `Parameters<typeof calc_NNNNNN>` / `ReturnType<...>` rather
 * than restating the signature. The three sheets families take genuinely different
 * second arguments (`xbrlPrefix`, a `suffixes` array, and role 19's extra `options`
 * object), and re-declaring them here would let the facade drift from the
 * implementation silently. This way a signature change cannot bypass the facade.
 */
@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  /** Role 8 — Statement of Financial Position. Implementation: `calculation/role8-210000.calc.ts`. */
  calculateTotals_210000(
    ...args: Parameters<typeof calc_210000>
  ): ReturnType<typeof calc_210000> {
    return calc_210000(...args);
  }

  /** Role 9 — Sub-classification of Assets, Liabilities and Equity. Implementation: `calculation/role9-210100.calc.ts`. */
  calculateTotals_210100(
    ...args: Parameters<typeof calc_210100>
  ): ReturnType<typeof calc_210100> {
    return calc_210100(...args);
  }

  /** Role 10 — Statement of Financial Position (Liquidity). Implementation: `calculation/role10-220000.calc.ts`. */
  calculateTotals_220000(
    ...args: Parameters<typeof calc_220000>
  ): ReturnType<typeof calc_220000> {
    return calc_220000(...args);
  }

  /** Role 11 — Sub-classification (Liquidity). Implementation: `calculation/role11-220100.calc.ts`. */
  calculateTotals_220100(
    ...args: Parameters<typeof calc_220100>
  ): ReturnType<typeof calc_220100> {
    return calc_220100(...args);
  }

  /** Role 12 — Statement of Profit or Loss. Implementation: `calculation/role12-310000.calc.ts`. */
  calculateTotals_310000(
    ...args: Parameters<typeof calc_310000>
  ): ReturnType<typeof calc_310000> {
    return calc_310000(...args);
  }

  /** Role 13 — Analysis of Profit or Loss. Implementation: `calculation/role13-310100.calc.ts`. */
  calculateTotals_310100(
    ...args: Parameters<typeof calc_310100>
  ): ReturnType<typeof calc_310100> {
    return calc_310100(...args);
  }

  /** Role 14 — Statement of Profit or Loss by Nature. Implementation: `calculation/role14-320000.calc.ts`. */
  calculateTotals_320000(
    ...args: Parameters<typeof calc_320000>
  ): ReturnType<typeof calc_320000> {
    return calc_320000(...args);
  }

  /** Role 15 — Analysis of Profit or Loss by Nature. Implementation: `calculation/role15-320100.calc.ts`. */
  calculateTotals_320100(
    ...args: Parameters<typeof calc_320100>
  ): ReturnType<typeof calc_320100> {
    return calc_320100(...args);
  }

  /** Role 15 — Statement of Comprehensive Income. Implementation: `calculation/role15-410000.calc.ts`. */
  calculateTotals_410000(
    ...args: Parameters<typeof calc_410000>
  ): ReturnType<typeof calc_410000> {
    return calc_410000(...args);
  }

  /** Role 16 — Statement of Comprehensive Income before Tax. Implementation: `calculation/role16-420000.calc.ts`. */
  calculateTotals_420000(
    ...args: Parameters<typeof calc_420000>
  ): ReturnType<typeof calc_420000> {
    return calc_420000(...args);
  }

  /** Role 17 — Statement of Cash Flows (Direct). Implementation: `calculation/role17-510000.calc.ts`. */
  calculateTotals_510000(
    ...args: Parameters<typeof calc_510000>
  ): ReturnType<typeof calc_510000> {
    return calc_510000(...args);
  }

  /** Role 18 — Statement of Cash Flows (Indirect). Implementation: `calculation/role18-520000.calc.ts`. */
  calculateTotals_520000(
    ...args: Parameters<typeof calc_520000>
  ): ReturnType<typeof calc_520000> {
    return calc_520000(...args);
  }

  /** Role 19 — Statement of Changes in Equity. Implementation: `calculation/role19-610000.calc.ts`. */
  calculateTotals_610000(
    ...args: Parameters<typeof calc_610000>
  ): ReturnType<typeof calc_610000> {
    return calc_610000(...args);
  }
}
